/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import apiClient from './client';
import { APIResponse, DocumentDTO } from '@/types';
import { DriverLicenseRequirement } from './ownerSettings';

/**
 * One additional service (add-on) ordered on the booking, as it appears on the contract.
 * The backend resolves these from the booking at read time and has ALREADY added their amounts to
 * `subtotal` / `totalCharges` / `finalTotal` — render the lines, never re-add them to the total.
 */
export interface AgreementServiceLine {
  additionalServiceId?: string;
  /** Name snapshotted on the booking — the fallback when no translation covers the contract language. */
  name: string;
  /** Multilingual {lang: text} JSON from the owner's catalog; pick the language the contract is issued in. */
  nameJson?: string | null;
  /** Unit price; for the `percent` cadence this is the percentage. */
  price: number;
  /** AddOnPricing cadence: one_time/per_minute/per_hour/per_day/per_week/per_month/percent. */
  pricingType: string;
  quantity: number;
  currency?: string;
  /** The line's charge over the whole rental window. */
  amount: number;
}

/**
 * The exact toll and violation fees the agreement's OWNER bills (server: AgreementFeeTerms, read from the
 * same OwnerBalance settings the charge code uses). Clause 7(f) quotes them instead of the generic
 * "typically $30 / up to $42" text, so the fee on the card statement is one the renter signed for.
 */
export interface AgreementFeeTerms {
  currency?: string;
  tollServiceFee: number;
  /** 'PerToll' | 'PerBooking' */
  tollFeeMode: string;
  tollBilledAtMaxRate?: boolean;
  tollAmountFromGps?: boolean;
  violationChargingEnabled: boolean;
  /** 'Fixed' ($ per citation) | 'Percent' (% of the fine) */
  violationFeeMode: string;
  violationFee: number;
  /** % of the TOTAL charged taken by card processing (2.9). */
  cardProcessingPercent: number;
  /** Fixed card processing amount per charge (0.30). */
  cardProcessingFixed: number;
}

export interface RentalAgreementData {
  // Existing fields for compatibility
  id?: string;
  customerName?: string;
  companyName: string;
  bookingId: string;
  vehicle?: string;
  plateNumber?: string;
  rentalPeriod?: string;
  totalAmount?: string | number;
  tollPaymentMethod?: string;
  terms?: string[];
  dateFrom?: string;
  dateTo?: string;
  carId?: string;
  ownerId?: string;
  driverEmail?: string;
  driverPhone?: string;
  preferredLanguage?: string;
  ownerName?: string;
  ownerCompanyName?: string;

  // Enhanced customer fields from aegis-aa-web
  userId?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  // Original-language (native-script) name + flag — set by the backend when the agreement's OWNER
  // company is in a non-English country. Used to render the renter's name in the license's own language.
  firstNameLocal?: string;
  lastNameLocal?: string;
  middleNameLocal?: string;
  preferOriginalScript?: boolean;
  /**
   * The OWNER company's jurisdiction language, resolved server-side from its country
   * (LicenseLanguagePolicy.ResolveJurisdictionLanguage): English for a US/UK/CA/AU/NZ/IE company, that
   * country's language otherwise, English when we ship no translation for it.
   *
   * The PDF always leads with the language the driver read and signed in; a full copy in THIS language is
   * appended when the two differ. The on-screen form is unaffected — it stays in a single language.
   */
  contractJurisdictionLanguage?: string;
  /**
   * The OWNER company sits in the United States (LicenseLanguagePolicy.IsUnitedStates).
   *
   * Section 4 of the terms carries per-US-state damage-waiver disclosures — California, Illinois,
   * Indiana, Nevada, New York, Wisconsin — which US law imposes on a US lessor. A company outside the
   * US owes none of them, so those paragraphs are dropped from its contracts. Absent (older API) reads
   * as true, which is the document every contract signed so far received.
   */
  ownerInUnitedStates?: boolean;
  /** Owner-specific fees for clause 7(f). Absent (older API / unresolved) keeps the generic clause. */
  feeTerms?: AgreementFeeTerms | null;
  email: string;
  phoneNumber: string;
  secondPhoneNumber?: string;
  driverLicense: string;
  state: string;
  licenseExpirationDate: string;
  dateOfBirth: string;
  address: string;

  // Additional Driver
  additionalDriver?: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    secondPhoneNumber?: string;
    driverLicense?: string;
    state?: string;
    licenseExpirationDate?: string;
    dateOfBirth?: string;
    address?: string;
  };

  // Vehicle Information
  vehicleType?: string;
  unitNumber?: string;
  makeAndModel: string;
  yearColorLicense: string;
  vin: string;
  odometer: number;

  // Replacement Vehicle
  replacementVehicle?: {
    vehicleType?: string;
    unitNumber?: string;
    makeAndModel?: string;
    yearColorLicense?: string;
    vin?: string;
    odometer?: number;
  };

  // Date and Time of Exchange
  exchangeDate?: string;
  exchangeTime?: string;
  exchangeAmPm?: string;

  // Rental Period
  startDate: string;
  startTime?: string;
  startAmPm?: string;
  dueDate?: string;
  dueTime?: string;
  dueAmPm?: string;
  endDate?: string;
  returnTime?: string;
  returnAmPm?: string;

  // Fuel Levels
  fuelLevelPickup?: string;
  fuelLevelReturn?: string;
  refuelingChargePerGallon?: number;

  // Security Deposit
  securityDeposit: number;
  depositCardNumber?: string;
  depositCardName?: string;
  depositCardExp?: string;
  depositCardCode?: string;
  depositBillingZip?: string;
  depositCashAmount?: number;

  // Rental Rates
  pricingPeriod?: number;
  ratePerDay?: number;
  numberOfDays?: number;
  dailyTotal?: number;
  ratePerWeek?: number;
  numberOfWeeks?: number;
  weeklyTotal?: number;
  surchargeTaxPerDay?: number;
  surchargeTaxTotal?: number;
  pickupDropoffDelivery?: number;
  cdwPerDay?: number;
  cdwTotal?: number;
  gpsPerDay?: number;
  gpsTotal?: number;
  childSeatPerDay?: number;
  childSeatTotal?: number;
  driverUnder25PerDay?: number;
  driverUnder25Total?: number;
  additionalDriverPerDay?: number;
  additionalDriverTotal?: number;
  adjustment?: number;
  creditCardFee?: number;
  subtotal?: number;
  vehicleStateTaxPercent?: number;
  vehicleStateTax?: number;
  totalCharges: number;
  finalTotal?: number;

  /** Add-ons ordered on the booking. Their amounts are already inside subtotal/totalCharges/finalTotal. */
  additionalServices?: AgreementServiceLine[];
  additionalServicesTotal?: number;

  // Extensions
  extensions?: Array<{
    date: string;
    amount: number;
  }>;

  // Additional Charges and Refunds
  lateReturnFee?: number;
  additionalCharge1?: { description?: string; amount?: number };
  additionalCharge2?: { description?: string; amount?: number };
  damageFee?: number;
  fuelServiceFee?: number;
  cleaningFee?: number;
  additionalCreditCardFee?: number;
  additionalVehicleStateTax?: number;
  refund?: number;
  additionalTotalCharges?: number;
  securityDepositRefunded?: number;
  balanceDue?: number;

  // Created Date
  createdDate?: string;

  // Page 2 - Renter's Personal Vehicle Insurance
  renterInsurance?: {
    company?: string;
    exp?: string;
    policyNumber?: string;
    phone?: string;
  };

  // Page 2 - Additional Info
  howDidYouHear?: string;
  withWhomEmployed?: string;

  // Page 2 - Rules Initials (checkboxes)
  initialsProhibitedDriver?: boolean;
  initialsUnder25?: boolean;
  initialsAlcohol?: boolean;
  initialsNoSmoking?: boolean;
  initialsLostKeys?: boolean;
  initialsPassengerCapacity?: boolean;
  initialsCleaningFee?: boolean;
  initialsTires?: boolean;
  initialsTickets?: boolean;
  initials24Hour?: boolean;
  initialsNoCellPhone?: boolean;

  // Page 2 - Payment Form on File
  paymentCardOnFile?: {
    nameOnCard?: string;
    cardNumber?: string;
    exp?: string;
    cvvCode?: string;
    cardType?: string;
    billingZip?: string;
  };

  // Page 2 - Renter's Card
  renterCard?: {
    nameOnCard?: string;
    cardNumber?: string;
    exp?: string;
    cvvCode?: string;
    cardType?: string;
    billingZip?: string;
  };

  // Page 2 - Signature fields
  agreementDate?: string;
  /** The signing instant (ISO, set by the page when it builds the signed PDF) — printed with time and zone. */
  signedAt?: string;
  /** The renter's device time zone the signing time is printed in. */
  signedTimeZone?: string;
  smokingFine?: number;
  cleaningFeeMax?: number;

  // Additional flat fields for backward compatibility with API
  last4Digits?: string;
  expirationDate?: string;
  insuranceCompany?: string;
  insuranceExpirationDate?: string;
  policyNumber?: string;
  insurancePhone?: string;
  nameOnCard?: string;
  cardType?: string;
  billingZip?: string;
  shortenedAgreement?: boolean;
  /** "Rental" (booking-bound, default) or "Driver" (rules-of-conduct only). */
  agreementType?: 'Rental' | 'Driver';
}

export interface AcceptAgreementRequest {
  requestId: string;
  acceptedAt: string;
  ipAddress?: string;
  userAgent?: string;
  /** The renter's device time zone (IANA) — recorded with the signing. */
  timeZone?: string;
  /** The language the renter read and signed in. */
  language?: string;
  signedPdfFile: File | Blob;
}

export interface AcceptAgreementResponse {
  success: boolean;
  message: string;
  agreementId: string;
  needsCard?: boolean;
  reason?: string;
}

export const agreementApi = {
  // Get agreements for user (like WPF Owner)
  getAgreementsByUser: async (userId: string): Promise<APIResponse<DocumentDTO[]>> => {
    console.log(`📡 Making API request: GET /Agreement/get-agreements-by-user/${userId}`);

    // Log request details (like WPF Owner debugging)
    console.log('📡 Request Details:', {
      method: 'GET',
      url: `/Agreement/get-agreements-by-user/${userId}`,
      baseURL: apiClient.defaults.baseURL,
      headers: {
        ...apiClient.defaults.headers,
        'Authorization': 'Bearer [HIDDEN]'
      }
    });

    try {
      const response = await apiClient.get<APIResponse<DocumentDTO[]>>(
        `/Agreement/get-agreements-by-user/${userId}`
      );

      console.log('📡 API Response Status:', response.status);
      console.log('📡 API Response Headers:', response.headers);
      console.log('📡 API Response Data (Full):', response.data);

      return response.data;
    } catch (error: any) {
      console.error('📡 API Request Failed:', error);

      // Check if it's a 404 - endpoint doesn't exist
      if (error.response?.status === 404) {
        console.error('📡 Endpoint not found - may not exist in current API version');
      }

      // Return empty result instead of throwing
      return {
        result: [],
        reason: error.response?.status || 10000,
        message: error.response?.data?.message || error.message || 'API request failed'
      };
    }
  },

  getAgreement: async (requestId: string): Promise<APIResponse<RentalAgreementData>> => {
    const response = await apiClient.get<any>(
      `/Agreement/get-agreement-data/${requestId}`
    );

    // Handle API response structure from aegis-aa-web: { result: {...}, reason: 0, message: string }
    if (response.data?.result && response.data?.reason === 0) {
      return {
        result: response.data.result,
        reason: response.data.reason,
        message: response.data.message || '',
        stackTrace: response.data.stackTrace
      };
    } else {
      // Return empty agreement data instead of null to satisfy TypeScript
      const emptyAgreement: RentalAgreementData = {
        companyName: "",
        bookingId: "",
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        driverLicense: "",
        state: "",
        licenseExpirationDate: "",
        dateOfBirth: "",
        address: "",
        makeAndModel: "",
        yearColorLicense: "",
        vin: "",
        odometer: 0,
        startDate: "",
        securityDeposit: 0,
        totalCharges: 0
      };

      return {
        result: emptyAgreement,
        reason: response.data?.reason || 10000,
        message: response.data?.message || 'Agreement not found',
        stackTrace: response.data.stackTrace
      };
    }
  },

  acceptAgreement: async (data: AcceptAgreementRequest): Promise<APIResponse<AcceptAgreementResponse>> => {
    console.log('=== ACCEPT AGREEMENT DEBUG ===');
    console.log('RequestId:', data.requestId);
    console.log('AcceptedAt:', data.acceptedAt);
    console.log('SignedPdfFile size:', data.signedPdfFile.size, 'bytes');
    console.log('SignedPdfFile type:', data.signedPdfFile.type);

    const formData = new FormData();
    // API expects fields with capital letters (per .NET ModelState validation)
    formData.append('RequestId', data.requestId);
    formData.append('AcceptedAt', data.acceptedAt);
    if (data.ipAddress) formData.append('IpAddress', data.ipAddress);
    if (data.userAgent) formData.append('UserAgent', data.userAgent);
    if (data.timeZone) formData.append('TimeZone', data.timeZone);
    if (data.language) formData.append('Language', data.language);

    // Create a proper File object from the blob to ensure correct content type
    const filename = `signed-agreement-${data.requestId}.pdf`;
    const pdfFile = new File([data.signedPdfFile], filename, {
      type: 'application/pdf',
      lastModified: Date.now()
    });
    formData.append('SignedPdfFile', pdfFile);

    console.log('FormData fields:');
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? `File(${value.name}, ${value.size}bytes)` : value);
    }

    // Use the correct endpoint for owner web - this endpoint accepts agreement AND changes booking status
    const response = await apiClient.post<APIResponse<AcceptAgreementResponse>>(
      `/rental-agreement/${data.requestId}/accept`,
      formData,
      {
        headers: {
          // Remove Content-Type to let browser set multipart/form-data with boundary
          'Content-Type': undefined
        },
        timeout: 60000 // 60 second timeout for file upload
      }
    );

    console.log('=== ACCEPT AGREEMENT RESPONSE ===', response.status, response.data);
    return response.data;
  },

  // Get signed agreement PDF URL by booking ID
  getAgreementPdfUrl: async (bookingId: string): Promise<string | null> => {
    try {
      const response = await apiClient.get<APIResponse<string>>(
        `/Agreement/get-agreement-pdf-url/${bookingId}`
      );
      return response.data.result || null;
    } catch {
      return null;
    }
  },

  // Batch: get signed agreement PDF URLs for multiple bookings in one request
  getAgreementPdfUrlsBatch: async (bookingIds: string[]): Promise<Record<string, string>> => {
    try {
      const response = await apiClient.post<APIResponse<Record<string, string>>>(
        '/Agreement/get-agreement-pdf-urls-batch',
        bookingIds
      );
      return response.data.result || {};
    } catch {
      return {};
    }
  },

  // Create agreement from booking data (backend loads all data from DB)
  createAgreementFromBooking: async (bookingId: string, ownerId: string, recipientEmail?: string): Promise<APIResponse<{ success: boolean; smsDeliveryMethod: string; driverPhone: string; agreementUrl?: string; emailSent?: boolean }>> => {
    const params = new URLSearchParams({ ownerId });
    if (recipientEmail) params.append('recipientEmail', recipientEmail);
    const response = await apiClient.post<APIResponse<{ success: boolean; smsDeliveryMethod: string; driverPhone: string; agreementUrl?: string; emailSent?: boolean }>>(
      `/Agreement/create-agreement-from-booking/${encodeURIComponent(bookingId)}?${params.toString()}`
    );
    return response.data;
  },

  // Skip agreement requirement (set AgreementSigned flag without actual agreement)
  skipAgreement: async (bookingId: string): Promise<APIResponse<boolean>> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      `/Agreement/skip-agreement/${encodeURIComponent(bookingId)}`
    );
    return response.data;
  },

  // Create card registration link (no agreement needed).
  // recipientEmail overrides the address the link is mailed to — the operator may pick one of the
  // driver's additional contacts instead of their primary address.
  createCardRegistrationLink: async (bookingId: string, ownerId: string, recipientEmail?: string): Promise<APIResponse<{ success: boolean; messageDeliveryMethod: string; driverPhone: string; cardRegistrationUrl?: string; emailSent?: boolean }>> => {
    const params = new URLSearchParams({ ownerId });
    if (recipientEmail) params.append('recipientEmail', recipientEmail);
    const response = await apiClient.post<APIResponse<{ success: boolean; messageDeliveryMethod: string; driverPhone: string; cardRegistrationUrl?: string; emailSent?: boolean }>>(
      `/Agreement/create-card-registration-link/${encodeURIComponent(bookingId)}?${params.toString()}`
    );
    return response.data;
  },

  // Create card registration link for a driver WITHOUT a booking (operator-initiated from Drivers page).
  // If recipientEmail is set, the server also sends a card-specific email (no booking/agreement wording).
  createCardRegistrationLinkForUser: async (userId: string, ownerId: string, recipientEmail?: string): Promise<APIResponse<{ success: boolean; cardRegistrationUrl: string; driverPhone: string; driverEmail: string; emailSent: boolean }>> => {
    const params = new URLSearchParams({ ownerId });
    if (recipientEmail) params.append('recipientEmail', recipientEmail);
    const response = await apiClient.post<APIResponse<{ success: boolean; cardRegistrationUrl: string; driverPhone: string; driverEmail: string; emailSent: boolean }>>(
      `/Agreement/create-card-registration-link-for-user/${encodeURIComponent(userId)}?${params.toString()}`
    );
    return response.data;
  },

  // Complete card registration (called from AddCardPage)
  completeCardRegistration: async (bookingId: string): Promise<APIResponse<boolean>> => {
    const response = await apiClient.post<APIResponse<boolean>>(
      `/Agreement/complete-card-registration/${encodeURIComponent(bookingId)}`
    );
    return response.data;
  },

  // Operator-initiated: send a driver a link to sign their standalone (no-booking) agreement.
  // If recipientEmail is set, the server sends a properly-themed agreement email.
  createStandaloneAgreementLink: async (userId: string, ownerId: string, recipientEmail?: string): Promise<APIResponse<{ success: boolean; agreementUrl: string; driverPhone: string; driverEmail: string; emailSent: boolean }>> => {
    const params = new URLSearchParams({ ownerId });
    if (recipientEmail) params.append('recipientEmail', recipientEmail);
    const response = await apiClient.post<APIResponse<{ success: boolean; agreementUrl: string; driverPhone: string; driverEmail: string; emailSent: boolean }>>(
      `/Agreement/create-standalone-agreement-link/${encodeURIComponent(userId)}?${params.toString()}`
    );
    return response.data;
  },

  // Create standalone agreement for pre-registration (no booking)
  createStandaloneAgreement: async (userId: string, ownerId: string): Promise<APIResponse<{ agreementId: string }>> => {
    const response = await apiClient.post<APIResponse<{ agreementId: string }>>(
      '/Agreement/create-standalone-agreement',
      { userId, ownerId },
      { timeout: 120000 }
    );
    return response.data;
  },

  // Accept standalone agreement (upload signed PDF, no booking transitions)
  acceptStandaloneAgreement: async (agreementId: string, signedPdf: Blob): Promise<APIResponse<{ success: boolean }>> => {
    const formData = new FormData();
    formData.append('SignedPdfFile', signedPdf, 'signed-agreement.pdf');
    const response = await apiClient.post<APIResponse<{ success: boolean }>>(
      `/Agreement/accept-standalone-agreement/${encodeURIComponent(agreementId)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Get standalone agreement PDF URLs for all drivers of an owner
  getStandaloneAgreements: async (ownerId: string): Promise<APIResponse<Record<string, string>>> => {
    const response = await apiClient.get<APIResponse<Record<string, string>>>(
      `/Agreement/standalone-agreements/${encodeURIComponent(ownerId)}`
    );
    return response.data;
  },

  // Get public owner settings (agreement flags)
  getPublicOwnerSettings: async (ownerId: string): Promise<APIResponse<{ requireRentalAgreement: boolean; sendAgreementOnlyToNewUsers: boolean; requireAgreementOnRegistration: boolean; driverLicenseRequirement?: DriverLicenseRequirement }>> => {
    const response = await apiClient.get<APIResponse<{ requireRentalAgreement: boolean; sendAgreementOnlyToNewUsers: boolean; requireAgreementOnRegistration: boolean; driverLicenseRequirement?: DriverLicenseRequirement }>>(
      `/OwnerSettings/public/${encodeURIComponent(ownerId)}`
    );
    return response.data;
  },

  // Send owner agreement email (admin only)
  sendOwnerAgreementEmail: async (ownerId: string): Promise<any> => {
    const response = await apiClient.post(`/Agreement/send-owner-agreement/${encodeURIComponent(ownerId)}`);
    return response.data;
  },

  // Get owner agreement data for signing page (public)
  getOwnerAgreementData: async (authKey: string): Promise<any> => {
    const response = await apiClient.get(`/Agreement/owner-agreement-data/${encodeURIComponent(authKey)}`);
    return response.data;
  },

  // Accept owner agreement with signed PDF (public)
  acceptOwnerAgreement: async (authKey: string, pdfBlob: Blob): Promise<any> => {
    const formData = new FormData();
    formData.append('SignedPdfFile', new File([pdfBlob], 'owner-agreement.pdf', { type: 'application/pdf' }));
    const response = await apiClient.post(
      `/Agreement/accept-owner-agreement/${encodeURIComponent(authKey)}`,
      formData,
      { headers: { 'Content-Type': undefined }, timeout: 60000 }
    );
    return response.data;
  },
};